import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { FiRotateCw, FiMaximize, FiMinimize } from "react-icons/fi";
import { URL } from "../../../Redux/common/url";

const ChapterVideo = ({ post, setVideoCompleted }) => {
  const [play, setPlay] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(
    () => window.innerWidth || 360
  );

  const wrapperRef = useRef(null);

  const thumbnail = post?.thumbnail
    ? URL.API_BASE_URL + post.thumbnail
    : "";

  const rawVideo = post?.video || post?.link || "";

  // 🔥 Detect YouTube
  const isYoutube =
    rawVideo.includes("youtube.com") || rawVideo.includes("youtu.be");

  // 🔥 Detect MP4
  const isMp4 = rawVideo.endsWith(".mp4");

  // ✅ YouTube embed
  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";

    let id = "";

    if (url.includes("youtu.be")) {
      id = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("youtube.com")) {
      id = url.split("v=")[1]?.split("&")[0];
    }

    return id ? `https://www.youtube.com/embed/${id}` : "";
  };

  const youtubeUrl = getYoutubeEmbedUrl(rawVideo);

  const fileVideoUrl =
    rawVideo &&
    (rawVideo.startsWith("http")
      ? rawVideo
      : URL.API_BASE_URL + rawVideo);

  // ✅ Measure wrapper width (for non-fullscreen rotated view)
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const update = () => {
      if (wrapperRef.current) {
        setContainerWidth(wrapperRef.current.offsetWidth);
      }
    };

    update();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      ro.observe(el);
    }

    window.addEventListener("resize", update);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // ✅ Track fullscreen state
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    const el = wrapperRef.current;
    if (!el) return;

    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  };

  // ✅ Like YouTube: rotate button enters fullscreen + rotates 90°
  const handleRotate = () => {
    if (!document.fullscreenElement && wrapperRef.current) {
      wrapperRef.current.requestFullscreen?.().catch(() => {});
    }
    setRotation((r) => (r + 90) % 360);
  };

  const isSide = rotation === 90 || rotation === 270;

  // ✅ Box dimensions
  let boxW;
  let boxH;
  let objectFit;

  if (isFullscreen) {
    // Viewport units → always fills the screen exactly, no measurement needed
    boxW = isSide ? "100vh" : "100vw";
    boxH = isSide ? "100vw" : "100vh";
    objectFit = "cover";
  } else if (isSide) {
    boxW = 220;
    boxH = containerWidth || 220;
    objectFit = "cover";
  } else {
    boxW = "100%";
    boxH = 220;
    objectFit = "contain";
  }

  // ✅ YouTube iframe scaling (16:9) so rotated view has no black bars
  let ytWidth;
  let ytHeight;
  if (isYoutube && isSide) {
    const h = isFullscreen
      ? window.innerWidth
      : containerWidth || 220;
    ytWidth = Math.ceil(h * (16 / 9));
    ytHeight = h;
  }

  return (
    <div className="p-2">
      <p className="text-white my-1 mt-2 chapter-title">
        {post?.name || "--"}
      </p>

      <div
        ref={wrapperRef}
        style={{
          position: "relative",
          width: "100%",
          height: isFullscreen ? "100%" : 220,
          background: "#000",
          overflow: "hidden",
        }}
      >
        {!play ? (
          <div
            onClick={() => setPlay(true)}
            style={{
              cursor: "pointer",
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={thumbnail}
              className="img-fluid rounded w-100 h-100"
              alt="thumbnail"
            />

            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "40px",
                color: "#fff",
                background: "rgba(0,0,0,0.6)",
                borderRadius: "50%",
                padding: "10px 18px",
              }}
            >
              ▶
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                className={isSide ? "rotated-video" : ""}
                style={{
                  width: boxW,
                  height: boxH,
                  transform: `rotate(${rotation}deg)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {isYoutube ? (
                  <iframe
                    width={ytWidth || "100%"}
                    height={ytHeight || "100%"}
                    src={youtubeUrl}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="video"
                  />
                ) : isMp4 ? (
                  <video
                    src={fileVideoUrl}
                    controls
                    autoPlay
                    width="100%"
                    height="100%"
                    style={{ objectFit }}
                    onEnded={() => setVideoCompleted(true)}
                  />
                ) : (
                  <ReactPlayer
                    url={fileVideoUrl}
                    playing={true}
                    controls
                    width="100%"
                    height="100%"
                    onEnded={() => setVideoCompleted(true)}
                  />
                )}
              </div>
            </div>

            {/* ✅ Control buttons */}
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                gap: 8,
                zIndex: 20,
              }}
            >
              <button
                onClick={handleRotate}
                title="Rotate Video"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                <FiRotateCw />
              </button>

              <button
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                {isFullscreen ? <FiMinimize /> : <FiMaximize />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChapterVideo;
